/**
 * Typography tokens — single source of truth for every text style in the app.
 *
 * Mirrors the "Typography Tokens" frame in Figma (BeforeYouBuild file,
 * transitions.dev text styles).
 * Figma path: https://www.figma.com/design/tOkxmMumUQOCkfNSoThZLx/BeforeYouBuild?node-id=481-170
 *
 * NOTHING in the UI consumes these yet. Like `colorTokens.ts`, this is the
 * staging ground for a tactical, screen-by-screen typography migration in a
 * later session. To start consuming them:
 *   - In CSS: inject `typographyTokensCss()` once at the app root, then add
 *     the generated class (e.g. `type-heading-title`) to an element.
 *   - In TS/React: spread `typographyStyle(token)` into an inline style, or
 *     read the structured fields directly.
 *
 * Keep this the ONLY place these type specs live, so the .ts and any
 * generated stylesheet can never drift.
 *
 * Unit notes from the Figma source:
 *   - `lineHeight` is a CSS value: "48px" (absolute) or "1.3" (multiplier,
 *     shown as 130% in Figma).
 *   - `letterSpacing` is a CSS value: "0", "-0.03em" (Figma "-3%"), or
 *     "-0.1174px" (Label/Badge, Figma "-0.12px").
 */

export type FontFamilyKey = "Inter" | "Fraunces" | "Roboto Mono";

/** Resolved font stacks. Bare Figma family + sensible system fallbacks. */
export const FONT_STACKS: Record<FontFamilyKey, string> = {
  Inter: '"Inter", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
  Fraunces: '"Fraunces", Georgia, serif',
  "Roboto Mono":
    '"Roboto Mono", ui-monospace, SFMono-Regular, Menlo, Consolas, monospace'
};

export interface TypographyToken {
  /** Figma variable path, e.g. "Heading/Title". Stable identity. */
  readonly path: string;
  /** Display group from the Figma frame, e.g. "Heading". */
  readonly group: string;
  /** Leaf name within the group, e.g. "Title". */
  readonly name: string;
  /** CSS class name for the generated utility, e.g. "type-heading-title". */
  readonly className: string;
  /** Raw Figma font family key. */
  readonly family: FontFamilyKey;
  /** Full font stack (family + fallbacks). */
  readonly fontFamily: string;
  /** Figma style label, e.g. "Semi Bold". */
  readonly styleName: string;
  /** Numeric weight, e.g. 600. */
  readonly fontWeight: number;
  /** Font size in px. */
  readonly fontSize: number;
  /** CSS line-height value ("48px" or unitless multiplier "1.3"). */
  readonly lineHeight: string;
  /** CSS letter-spacing value ("0", "-0.03em", "-0.1174px"). */
  readonly letterSpacing: string;
  /** Optional CSS text-transform (e.g. "uppercase" for eyebrows). */
  readonly textTransform?: string;
  /** Optional CSS font-variant-numeric (e.g. "tabular-nums" for data). */
  readonly fontVariantNumeric?: string;
  /** Human-readable spec line straight from Figma (for docs/showcase). */
  readonly spec: string;
  /** Sample copy used in the Figma frame. */
  readonly sample: string;
  /** Optional caveat. */
  readonly note?: string;
}

type TypographyTokenSeed = Omit<
  TypographyToken,
  "className" | "fontFamily"
>;

/**
 * Raw token specs, in Figma frame order. This is the editable source;
 * everything else is derived from it.
 */
const SEED: readonly TypographyTokenSeed[] = [
  {
    path: "Display/H1",
    group: "Display",
    name: "H1",
    family: "Fraunces",
    styleName: "Medium",
    fontWeight: 500,
    fontSize: 64,
    lineHeight: "64px",
    letterSpacing: "-0.02em",
    spec: "Fraunces Medium · 64/64px · -2%",
    sample: "Know if a lot is buildable\nbefore you make an offer."
  },
  {
    path: "Display/H2",
    group: "Display",
    name: "H2",
    family: "Fraunces",
    styleName: "Medium",
    fontWeight: 500,
    fontSize: 48,
    lineHeight: "48px",
    letterSpacing: "-0.02em",
    spec: "Fraunces Medium · 48/48px · -2%",
    sample: "Six lenses. One living model."
  },
  {
    path: "Display/H3",
    group: "Display",
    name: "H3",
    family: "Fraunces",
    styleName: "Semi Bold",
    fontWeight: 600,
    fontSize: 32,
    lineHeight: "32px",
    letterSpacing: "-0.02em",
    spec: "Fraunces SemiBold · 32/32px · -2%",
    sample: "Six lenses. One living model."
  },
  {
    path: "Display/Amount",
    group: "Display",
    name: "Amount",
    family: "Inter",
    styleName: "Medium",
    fontWeight: 500,
    fontSize: 48,
    lineHeight: "48px",
    letterSpacing: "0",
    spec: "Inter Medium · 48/48px · 0",
    sample: "$1,248,000"
  },
  {
    path: "Display/Small",
    group: "Display",
    name: "Small",
    family: "Fraunces",
    styleName: "Medium",
    fontWeight: 500,
    fontSize: 24,
    lineHeight: "32px",
    letterSpacing: "-0.03em",
    spec: "Fraunces Medium · 24/32px · -3%",
    sample: "Before You Build"
  },
  {
    path: "Number/Small",
    group: "Number",
    name: "Small",
    family: "Fraunces",
    styleName: "Semi Bold",
    fontWeight: 600,
    fontSize: 30,
    lineHeight: "40px",
    letterSpacing: "-0.03em",
    spec: "Fraunces SemiBold · 30/40px · -3%",
    sample: "$2,500"
  },
  {
    path: "Heading/Title",
    group: "Heading",
    name: "Title",
    family: "Inter",
    styleName: "Medium",
    fontWeight: 500,
    fontSize: 22,
    lineHeight: "32px",
    letterSpacing: "-0.01em",
    spec: "Inter Medium · 22/32px · -1%",
    sample: "Property overview"
  },
  {
    path: "Heading/Section",
    group: "Heading",
    name: "Section",
    family: "Inter",
    styleName: "Medium",
    fontWeight: 500,
    fontSize: 18,
    lineHeight: "1.3",
    letterSpacing: "-0.01em",
    spec: "Inter Medium · 18/130% · -1%",
    sample: "Structures"
  },
  {
    path: "Heading/Numeric",
    group: "Heading",
    name: "Numeric",
    family: "Inter",
    styleName: "Medium",
    fontWeight: 500,
    fontSize: 24,
    lineHeight: "24px",
    letterSpacing: "0",
    spec: "Inter Medium · 24/24px · 0",
    sample: "2,107 sf"
  },
  {
    path: "Body/Large",
    group: "Body",
    name: "Large",
    family: "Inter",
    styleName: "Regular",
    fontWeight: 400,
    fontSize: 15,
    lineHeight: "22px",
    letterSpacing: "0",
    spec: "Inter Regular · 15/22px · 0",
    sample: "Inferred from public data—confirm with local utilities."
  },
  {
    path: "Body/Large-Heavy",
    group: "Body",
    name: "Large-Heavy",
    family: "Inter",
    styleName: "Semi Bold",
    fontWeight: 600,
    fontSize: 15,
    lineHeight: "22px",
    letterSpacing: "0",
    spec: "Inter Semi Bold · 15/22px · 0",
    sample: "Inferred from public data—confirm with local utilities."
  },
  {
    path: "Body/Default",
    group: "Body",
    name: "Default",
    family: "Inter",
    styleName: "Regular",
    fontWeight: 400,
    fontSize: 14,
    lineHeight: "20px",
    letterSpacing: "0",
    spec: "Inter Regular · 14/20px · 0",
    sample: "Inferred from public data—confirm with local utilities."
  },
  {
    path: "Body/Default-Heavy",
    group: "Body",
    name: "Default-Heavy",
    family: "Inter",
    styleName: "Medium",
    fontWeight: 500,
    fontSize: 14,
    lineHeight: "24px",
    letterSpacing: "0",
    spec: "Inter Medium · 14/24px · 0",
    sample: "Inferred from public data—confirm with local utilities."
  },
  {
    path: "Body/Small",
    group: "Body",
    name: "Small",
    family: "Inter",
    styleName: "Regular",
    fontWeight: 400,
    fontSize: 13,
    lineHeight: "18px",
    letterSpacing: "0",
    spec: "Inter Regular · 13/18px · 0",
    sample: "Inferred from public data—confirm with local utilities."
  },
  {
    path: "Body/Small-Heavy",
    group: "Body",
    name: "Small-Heavy",
    family: "Inter",
    styleName: "Semi Bold",
    fontWeight: 600,
    fontSize: 13,
    lineHeight: "18px",
    letterSpacing: "0",
    spec: "Inter Semi Bold · 13/18px · 0",
    sample: "Single"
  },
  {
    path: "Body/Tiny",
    group: "Body",
    name: "Tiny",
    family: "Inter",
    styleName: "Regular",
    fontWeight: 400,
    fontSize: 11,
    lineHeight: "1.4",
    letterSpacing: "0",
    spec: "Inter Regular · 11/140% · 0",
    sample:
      "Placed a 1,848 sf pad on the flattest 0.22 ac shelf · 5.7% avg slope · 56 ft to Crow Hill Road · ~3 cu yd net fill"
  },
  {
    path: "Label/Default",
    group: "Label",
    name: "Default",
    family: "Inter",
    styleName: "Medium",
    fontWeight: 500,
    fontSize: 14,
    lineHeight: "16px",
    letterSpacing: "0",
    spec: "Inter Medium · 14/16px · 0",
    sample: "Parcel boundary"
  },
  {
    path: "Label/Small",
    group: "Label",
    name: "Small",
    family: "Inter",
    styleName: "Medium",
    fontWeight: 500,
    fontSize: 13,
    lineHeight: "16px",
    letterSpacing: "0",
    spec: "Inter Medium · 13/16px · 0",
    sample: "Lot dimensions"
  },
  {
    path: "Label/Badge",
    group: "Label",
    name: "Badge",
    family: "Inter",
    styleName: "Medium",
    fontWeight: 500,
    fontSize: 11.744,
    lineHeight: "11.744px",
    letterSpacing: "-0.1174px",
    spec: "Inter Medium · 11.74/12px · -0.12px",
    sample: "PRO",
    note: "Odd size/spacing inherited from the source style (auto-scaled badge)."
  },
  {
    path: "Card/Title",
    group: "Card",
    name: "Title",
    family: "Inter",
    styleName: "Medium",
    fontWeight: 500,
    fontSize: 13,
    lineHeight: "18px",
    letterSpacing: "0",
    spec: "Inter Medium · 13/18px · 0",
    sample: "Sun path arc"
  },
  {
    path: "Card/Subtitle",
    group: "Card",
    name: "Subtitle",
    family: "Inter",
    styleName: "Regular",
    fontWeight: 400,
    fontSize: 13,
    lineHeight: "13px",
    letterSpacing: "0",
    spec: "Inter Regular · 13/13px · 0",
    sample: "Frontage on Crow Hill Road"
  },
  {
    path: "Button/Default",
    group: "Button",
    name: "Default",
    family: "Inter",
    styleName: "Semi Bold",
    fontWeight: 600,
    fontSize: 14,
    lineHeight: "24px",
    letterSpacing: "0",
    spec: "Inter Semi Bold · 14/24px · 0",
    sample: "Set viewpoint"
  },
  {
    path: "Button/Large",
    group: "Button",
    name: "Large",
    family: "Inter",
    styleName: "Medium",
    fontWeight: 500,
    fontSize: 18,
    lineHeight: "24px",
    letterSpacing: "0",
    spec: "Inter Medium · 18/24px · 0",
    sample: "Refresh external data"
  },
  {
    path: "Caption",
    group: "Utility",
    name: "Caption",
    family: "Inter",
    styleName: "Regular",
    fontWeight: 400,
    fontSize: 12,
    lineHeight: "16px",
    letterSpacing: "0",
    spec: "Inter Regular · 12/16px · 0",
    sample: "DISPLAY"
  },
  {
    path: "Mono/Code",
    group: "Utility",
    name: "Code",
    family: "Roboto Mono",
    styleName: "Regular",
    fontWeight: 400,
    fontSize: 12,
    lineHeight: "1.6",
    letterSpacing: "0",
    spec: "Roboto Mono Regular · 12/160% · 0",
    sample: "const sf = 2107;"
  },
  {
    path: "Mono/Data",
    group: "Mono",
    name: "Data",
    family: "Roboto Mono",
    styleName: "Medium",
    fontWeight: 500,
    fontSize: 11.5,
    lineHeight: "16px",
    letterSpacing: "0",
    fontVariantNumeric: "tabular-nums",
    spec: "Roboto Mono Medium · 11.5/16px · 0 · tabular",
    sample: "412.6 ft",
    note: "HUD readouts, measurements, metric values. Tabular numerals."
  },
  {
    path: "Mono/Eyebrow",
    group: "Mono",
    name: "Eyebrow",
    family: "Roboto Mono",
    styleName: "Semi Bold",
    fontWeight: 600,
    fontSize: 10.5,
    lineHeight: "14px",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    spec: "Roboto Mono Semi Bold · 10.5/14px · +8% · uppercase",
    sample: "SITE WORK",
    note: "Card headers, legend titles, HUD chip labels."
  },
  {
    path: "Mono/Display",
    group: "Mono",
    name: "Display",
    family: "Roboto Mono",
    styleName: "Medium",
    fontWeight: 500,
    fontSize: 14,
    lineHeight: "20px",
    letterSpacing: "0",
    fontVariantNumeric: "tabular-nums",
    spec: "Roboto Mono Medium · 14/20px · 0 · tabular",
    sample: "84/100",
    note: "Prominent numerals: buildability score chip, drawer section values."
  }
];

function classNameFor(path: string): string {
  return `type-${path.toLowerCase().replace(/\//g, "-")}`;
}

function toToken(seed: TypographyTokenSeed): TypographyToken {
  return {
    ...seed,
    className: classNameFor(seed.path),
    fontFamily: FONT_STACKS[seed.family]
  };
}

/** Flat list of every typography token, in Figma frame order. */
export const TYPOGRAPHY_TOKENS: readonly TypographyToken[] = SEED.map(toToken);

/** Group display order, matching the Figma frame layout. */
export const TYPOGRAPHY_TOKEN_GROUPS: readonly string[] = [
  ...new Set(TYPOGRAPHY_TOKENS.map((t) => t.group))
];

/** Tokens for a single group, in order. */
export function tokensForGroup(group: string): readonly TypographyToken[] {
  return TYPOGRAPHY_TOKENS.filter((t) => t.group === group);
}

/** Look up a token by its Figma path (e.g. "Heading/Title"). */
export function tokenByPath(path: string): TypographyToken | undefined {
  return TYPOGRAPHY_TOKENS.find((t) => t.path === path);
}

/** Inline-style object for React / canvas use. */
export function typographyStyle(token: TypographyToken): {
  fontFamily: string;
  fontWeight: number;
  fontSize: string;
  lineHeight: string;
  letterSpacing: string;
  textTransform?: string;
  fontVariantNumeric?: string;
} {
  return {
    fontFamily: token.fontFamily,
    fontWeight: token.fontWeight,
    fontSize: `${token.fontSize}px`,
    lineHeight: token.lineHeight,
    letterSpacing: token.letterSpacing,
    ...(token.textTransform ? { textTransform: token.textTransform } : {}),
    ...(token.fontVariantNumeric
      ? { fontVariantNumeric: token.fontVariantNumeric }
      : {})
  };
}

/**
 * Generate utility CSS classes (one per token). Inject this once at the app
 * root when the typography migration begins, then add the class to elements
 * (e.g. `class="type-heading-title"`).
 */
export function typographyTokensCss(): string {
  const utilities = TYPOGRAPHY_TOKENS.map((t) =>
    [
      `.${t.className} {`,
      `  font-family: ${t.fontFamily};`,
      `  font-weight: ${t.fontWeight};`,
      `  font-size: ${t.fontSize}px;`,
      `  line-height: ${t.lineHeight};`,
      `  letter-spacing: ${t.letterSpacing};`,
      ...(t.textTransform ? [`  text-transform: ${t.textTransform};`] : []),
      ...(t.fontVariantNumeric
        ? [`  font-variant-numeric: ${t.fontVariantNumeric};`]
        : []),
      `}`
    ].join("\n")
  ).join("\n\n");

  const h2 = tokenByPath("Display/H2");
  const h3 = tokenByPath("Display/H3");
  if (!h2 || !h3) return utilities;

  const mobileH2AsH3 = [
    "@media (max-width: 768px) {",
    `  h2.${h2.className} {`,
    `    font-family: ${h3.fontFamily};`,
    `    font-weight: ${h3.fontWeight};`,
    `    font-size: ${h3.fontSize}px;`,
    `    line-height: ${h3.lineHeight};`,
    `    letter-spacing: ${h3.letterSpacing};`,
    `  }`,
    `}`
  ].join("\n");

  return `${utilities}\n\n${mobileH2AsH3}`;
}
